import type { Patient, Practitioner, Reference } from '@medplum/fhirtypes'
import type { Participant } from '@seen/core/models/patients/participant'
import { PractitionerNotepad } from '@seen/core/models/practitionerNotepad'
import { IconInfoCircle } from '@tabler/icons-react'
import type { Editor } from '@tiptap/react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconNotepad } from '#src/components/icons/IconNotepad.tsx'
import {
  FacesheetCard,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
  reportFacesheetNotepadSaved,
  useFacesheetTab,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCreateIfNoneExistsMutation } from '#src/hooks/useCreateIfNoneExistsMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

const SAVE_ERROR = 'Could not save your notepad'

export interface MyNotepadCardProps {
  participant: Participant
}

// The current practitioner's own notepad on this participant, stored as a
// PractitionerNotepad Composition. The shared Notepad on Highlights is a
// questionnaire response everyone edits.
export function MyNotepadCard({
  participant,
}: MyNotepadCardProps): JSX.Element {
  const author = useCurrentPractitioner().reference
  const facesheetTab = useFacesheetTab()

  const notepadResult = useSearchOne(
    PractitionerNotepad,
    PractitionerNotepad.searchQuery({
      participant: participant.reference,
      author,
    })
  )

  return (
    <FacesheetCard rumName="my_notepad">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            <IconNotepad />
          </FacesheetCardIcon>
          <FacesheetCardLabel>My notepad</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
          <Tooltip>
            <TooltipTrigger
              aria-label="About my notepad"
              className="opacity-0 group-hover/facesheet-card:opacity-100 focus-visible:opacity-100"
            >
              <IconInfoCircle className="size-3" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>Your own notepad on this participant</p>
            </TooltipContent>
          </Tooltip>
        </FacesheetCardTitle>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        <MyNotepadEditor
          key={participant.id}
          participant={participant.reference}
          author={author}
          notepad={notepadResult.data ?? undefined}
          loading={notepadResult.isLoading}
          lookupFailed={notepadResult.isError}
          onSaved={() =>
            reportFacesheetNotepadSaved({
              participantId: participant.id,
              tab: facesheetTab?.tab,
              notepad: 'mine',
            })
          }
        />
      </FacesheetCardContent>
    </FacesheetCard>
  )
}

interface MyNotepadEditorProps {
  participant: Reference<Patient>
  author: Reference<Practitioner>
  notepad?: PractitionerNotepad
  loading: boolean
  // Saving after a failed lookup could overwrite the notepad it missed.
  lookupFailed: boolean
  onSaved: () => void
}

// Creates the notepad on the first save rather than on view, so practitioners
// who only open the tab leave nothing behind.
function MyNotepadEditor({
  participant,
  author,
  notepad,
  loading,
  lookupFailed,
  onSaved,
}: MyNotepadEditorProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const [created, setCreated] = useState<PractitionerNotepad>()
  const saved = notepad ?? created
  const savedRef = useCurrentRef(saved)
  // What the practitioner last typed, so the editor recreated for the saved
  // notepad starts from it rather than from a refetch that may lag behind.
  const draftRef = useRef<string | undefined>(undefined)
  const editorRef = useRef<Editor | null>(null)
  const onSavedRef = useCurrentRef(onSaved)
  // Saves that land while the first create is in flight patch what it returns
  // instead of creating again.
  const creatingRef = useRef<Promise<PractitionerNotepad | null> | null>(null)

  const { mutate: patch } = usePatchMutation<PractitionerNotepad>({
    mutation: {
      // Patches of one notepad run one at a time, as in ResourceFieldForm.
      scope: { id: `my-notepad:${participant.reference}` },
      onSuccess: () => onSavedRef.current(),
      onError: () => toast.error(SAVE_ERROR),
    },
  })
  const { mutateAsync: create } =
    useCreateIfNoneExistsMutation<PractitionerNotepad>()

  const patchContent = (instance: PractitionerNotepad, content: string) =>
    patch({
      instance,
      mutateFn: (n) => {
        n.content = content
      },
    })

  const save = useDebouncedCallback(async (content: string) => {
    if (isReadOnly || lookupFailed) {
      return
    }
    const existing = savedRef.current ?? (await creatingRef.current)
    if (existing) {
      patchContent(existing, content)
      return
    }
    creatingRef.current = create({
      instance: new PractitionerNotepad({ participant, author, content }),
      query: PractitionerNotepad.searchQuery({ participant, author }),
    }).catch(() => {
      creatingRef.current = null
      toast.error(SAVE_ERROR)
      return null
    })
    const result = await creatingRef.current
    if (!result) {
      return
    }
    setCreated(result)
    // A notepad another tab created first comes back without this content.
    if (result.content === content) {
      onSavedRef.current()
    } else {
      patchContent(result, content)
    }
  }, 2500)

  useEffect(() => () => void save.flush(), [save])

  return (
    <div className="grid text-foreground-secondary">
      {/* Keyed on the saved notepad because the file handler keeps the
       * `related` it mounted with. */}
      <RichTextEditor
        key={saved?.id ?? 'unsaved'}
        variant="underline"
        value={draftRef.current ?? saved?.content ?? ''}
        loading={loading}
        disabled={lookupFailed}
        // `autofocus` alone would focus the notepad whenever For you opens;
        // only the editor replacing a focused one takes over its focus.
        autofocus={editorRef.current?.isFocused ? 'end' : false}
        editorRef={editorRef}
        onChange={(content) => {
          draftRef.current = content
          void save(content)
        }}
        onBlur={() => void save.flush()}
        placeholder="Write a note for yourself..."
        related={saved ? [saved.reference] : undefined}
      />
    </div>
  )
}
